import { zodResolver } from '@hookform/resolvers/zod'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Country, State } from 'country-state-city'
import { Mail, User, UserPlus, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { z } from 'zod'

import type { Identity, Role } from '@ankor-io/common/types/account'
import {
  AWXCardholderType,
  type CreateCardholderRequest,
  type UserWithConnectedAccounts,
} from '@ankor-io/common/types/awx'
import type { Cardholder, CardholderSummary } from '@ankor-io/common/types/cards'

import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Dialog, DialogActions, DialogBody, DialogBodyAnimated, DialogTitle } from '@/ui-components/Dialog'
import { Field, Label } from '@/ui-components/Fieldset'
import { Input } from '@/ui-components/Input'
import { Select } from '@/ui-components/Select'
import type { OrganizationUser } from '@/views/Users/types'

import PhoneNumber from './PhoneNumber'

type AddNewCardholderModalProps = {
  isOpen: boolean
  onClose: () => void
  cardholders: CardholderSummary[]
  onNewCardholder: (cardholder: CardholderSummary) => void
}

const additionalUserInfoSchema = z
  .object({
    dateOfBirth: z.string().min(8, { message: 'Date of Birth is required' }),
    mobileNumber: z.string().min(1, { message: 'Mobile number is required' }),
    mobileNumberValid: z.boolean().refine((val) => val === true, {
      message: 'Please enter a valid phone number',
    }),
    address: z.object({
      line1: z.string().min(2, { message: 'Address line 1 is required' }),
      line2: z.string().optional(),
      city: z.string().min(2, { message: 'City is required' }),
      state: z.string().min(1, { message: 'State/Province is required' }),
      postcode: z.string().min(3, { message: 'Postcode is required' }),
      country: z.string().min(1, { message: 'Country is required' }),
    }),
  })
  .refine(
    (data) => {
      // TODO: Do we need to verify if the age is above 18?
      if (data.dateOfBirth) {
        return new Date(data.dateOfBirth) <= new Date()
      }
      return true
    },
    {
      message: 'Date of Birth cannot be in the future',
      path: ['dateOfBirth'],
    },
  )

type FormData = z.infer<typeof additionalUserInfoSchema>

// Get all countries and sort them alphabetically
const supportedCountries = Country.getAllCountries()
  .sort((a, b) => a.name.localeCompare(b.name))
  .filter((country) => {
    // Filter out countries that don't have any states/provinces
    const states = State.getStatesOfCountry(country.isoCode)
    return states.length > 0
  })
  .map((country) => ({
    code: country.isoCode,
    name: country.name,
  }))

const AddNewCardholderModal = ({ isOpen, onClose, cardholders, onNewCardholder }: AddNewCardholderModalProps) => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()

  const [isLoadingUsers, setIsLoadingUsers] = useState(true)
  const [users, setUsers] = useState<OrganizationUser[]>([])
  const [selectedUser, setSelectedUser] = useState<OrganizationUser | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [availableStates, setAvailableStates] = useState<{ code: string; name: string }[]>([])

  const form = useForm<FormData>({
    resolver: zodResolver(additionalUserInfoSchema),
    defaultValues: {
      dateOfBirth: '',
      mobileNumber: '',
      mobileNumberValid: false,
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        postcode: '',
        country: '',
      },
    },
  })

  // Watch for country changes to update available states
  const selectedCountry = form.watch('address.country')

  useEffect(() => {
    if (selectedCountry) {
      const states = State.getStatesOfCountry(selectedCountry)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((state) => ({
          code: state.isoCode || state.name, // Use ISO code if available, fallback to name
          name: state.name,
        }))
      setAvailableStates(states)

      // Reset state field when country changes
      form.setValue('address.state', '', { shouldValidate: true })
    } else {
      setAvailableStates([])
    }
  }, [selectedCountry, form])

  const getUsers = useCallback(async () => {
    if (!activeOrganizationId || !isOpen) {
      return
    }

    setIsLoadingUsers(true)
    const token = await getAccessToken()

    // Fetch both users and connected accounts in parallel
    const [usersResponse, connectedAccountsResponse] = await Promise.all([
      fetch(`/api/iam/org/${activeOrganizationId}/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      fetch(`/api/pay/org/${activeOrganizationId}/connected-accounts/user-access`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    ])

    let fetchedUsers: OrganizationUser[] = []
    let connectedAccountsData: { usersWithConnectedAccounts: UserWithConnectedAccounts[] } | null = null

    // Handle users response
    if (usersResponse.ok) {
      try {
        const data = (await usersResponse.json()) as { result: { identity: Identity; roles: Role[] }[] }
        fetchedUsers = data.result.map((user) => {
          return {
            id: user.identity.accountId,
            name: `${user.identity.firstName} ${user.identity.lastName}`,
            email: user.identity.email,
            role: user.roles.join(', '),
            connectedAccounts: [],
          }
        })
      } catch (error) {
        console.error('Error parsing users response:', error)
      }
    } else {
      console.error('Failed to fetch users:', usersResponse.status)
    }

    // Handle connected accounts response
    if (connectedAccountsResponse.ok) {
      try {
        connectedAccountsData = (await connectedAccountsResponse.json()) as {
          usersWithConnectedAccounts: UserWithConnectedAccounts[]
        }
      } catch (error) {
        console.error('Error parsing connected accounts response:', error)
      }
    } else {
      console.error('Failed to fetch connected accounts:', connectedAccountsResponse.status)
    }

    const currentConnectedAccountUsers = fetchedUsers.map((user) => {
      return {
        ...user,
        connectedAccounts:
          connectedAccountsData?.usersWithConnectedAccounts
            .find((u) => u.user.id === user.id)
            ?.connectedAccounts.map((connectedAccount) => connectedAccount.id) || [],
      }
    })

    // Filter out the users that don't have access to the active connected account
    const usersWithAccess = currentConnectedAccountUsers?.filter((user) =>
      user.connectedAccounts.some((connectedAccount) => connectedAccount === activeConnectedAccountId),
    )

    const usersThatAreNotCardholders = usersWithAccess.filter(
      (user) => !cardholders.some((cardholder) => cardholder.identity.accountId === user.id),
    )

    setUsers(usersThatAreNotCardholders)
    setIsLoadingUsers(false)
  }, [activeOrganizationId, activeConnectedAccountId, isOpen, getAccessToken, cardholders])

  useEffect(() => {
    getUsers()
  }, [getUsers])

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedUser(null)
      form.reset()
      setAvailableStates([])
    }
  }, [isOpen, form])

  // Handle user selection with animation
  const handleUserSelection = (userId: string) => {
    const user = users.find((user) => user.id === userId)
    setSelectedUser(user || null)
  }

  const onSubmit = async (values: FormData) => {
    if (!selectedUser) {
      return
    }

    setIsSubmitting(true)
    const savePromise = async () => {
      const token = await getAccessToken()
      const payload: CreateCardholderRequest = {
        userId: selectedUser.id,
        type: AWXCardholderType.INDIVIDUAL,
        mobile_number: values.mobileNumber,
        individual: {
          name: {
            first_name: selectedUser.name,
            last_name: selectedUser.name,
          },
          date_of_birth: values.dateOfBirth,
          address: values.address,
          express_consent_obtained: 'yes',
        },
      }

      const res = await fetch(
        `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/cardholder`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      )

      if (!res.ok) {
        setIsSubmitting(false)
        throw new Error('Failed to add cardholder')
      }

      const data = (await res.json()) as { cardholder: Cardholder }
      onNewCardholder({
        ...data.cardholder,
        identity: {
          accountId: data.cardholder.userId,
          firstName: selectedUser.name.split(' ')[0] || '',
          lastName: selectedUser.name.split(' ')[1] || '',
          email: selectedUser.email,
        },
      })

      setIsSubmitting(false)
      onClose()
    }

    await toast.promise(savePromise(), {
      loading: `Adding ${selectedUser?.name} as a cardholder...`,
      success: `${selectedUser?.name} added as a cardholder`,
      error: `Failed to add ${selectedUser?.name} as a cardholder. Please try again or contact support.`,
    })
  }

  return (
    <Dialog
      size="xl"
      open={isOpen}
      onClose={onClose}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <UserPlus className="size-5 text-gray-900 dark:text-gray-50" />
          <DialogTitle>Add new Cardholder</DialogTitle>
        </div>
        <Button
          plain
          onClick={onClose}
          aria-label="Close"
        >
          <X data-slot="icon" />
        </Button>
      </div>

      <DialogBody>
        {!isLoadingUsers && users.length === 0 ? (
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <UserPlus className="size-8 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No users found, or all users are already cardholders. Please add a new user to the vessel to continue
              creating the user as a cardholder.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* User Selection Section */}
            <div>
              <Field>
                <Label className="flex items-center gap-2 text-lg font-semibold">
                  <User className="size-5 text-primary-600 dark:text-primary-400" />
                  Select a user
                </Label>
                <Select
                  className="text-lg"
                  value={selectedUser?.id}
                  disabled={isLoadingUsers}
                  onChange={(e) => handleUserSelection(e.target.value)}
                >
                  {isLoadingUsers && <option value="">Loading users...</option>}
                  {!selectedUser && <option value="">Choose a user to add as cardholder</option>}
                  {users.map((user) => (
                    <option
                      key={user.id}
                      value={user.id}
                    >
                      {user.name} ({user.email})
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            {/* Form Section with Slide Animation */}
            {selectedUser && (
              <DialogBodyAnimated>
                {/* User Info Card */}
                <div className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary-100 dark:bg-primary-800 rounded-full flex items-center justify-center">
                      <User className="size-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 dark:text-white">{selectedUser.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Mail className="size-4" />
                        {selectedUser.email}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Fields with Dynamic Reveal */}
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  {/* Date of Birth and Mobile Number */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Field>
                        <Label htmlFor="dateOfBirth">Date of Birth</Label>
                        <Input
                          data-testid="date-of-birth"
                          id="dateOfBirth"
                          type="date"
                          {...form.register('dateOfBirth')}
                        />
                      </Field>
                    </div>

                    <Field>
                      <Label htmlFor="mobileNumber">Mobile Number</Label>
                      <div className="mt-3">
                        <Controller
                          name="mobileNumber"
                          control={form.control}
                          render={({ field }) => (
                            <PhoneNumber
                              value={field.value}
                              onChange={field.onChange}
                              onError={(isError: boolean) => {
                                form.setValue('mobileNumberValid', !isError, { shouldValidate: true })
                              }}
                            />
                          )}
                        />
                      </div>
                      {/* TODO: Add back in as part of form validation ticket (ISSUE-933) */}
                      {/* {form.formState.errors.mobileNumberValid && (
                        <ErrorMessage>{form.formState.errors.mobileNumberValid.message}</ErrorMessage>
                      )} */}
                    </Field>
                  </div>

                  {/* Address Section */}
                  <div className="space-y-4">
                    <Field>
                      <Label htmlFor="addressLine1">Address Line 1</Label>
                      <Input
                        id="addressLine1"
                        type="text"
                        {...form.register('address.line1')}
                        placeholder="Street address"
                      />
                    </Field>

                    <Field>
                      <Label htmlFor="addressLine2">Address Line 2 (optional)</Label>
                      <Input
                        id="addressLine2"
                        type="text"
                        {...form.register('address.line2')}
                        placeholder="Apartment, suite, etc."
                      />
                    </Field>

                    <div className="grid md:grid-cols-2 gap-4">
                      <Field>
                        <Label htmlFor="country">Country</Label>
                        <Select
                          id="country"
                          className="text-lg"
                          value={form.watch('address.country')}
                          onChange={(e) => form.setValue('address.country', e.target.value)}
                        >
                          {!form.watch('address.country') && <option value="">Select a country</option>}
                          {supportedCountries.map((country) => (
                            <option
                              key={country.code}
                              value={country.code}
                            >
                              {country.name}
                            </option>
                          ))}
                        </Select>
                      </Field>

                      <Field>
                        <Label htmlFor="state">State/Province</Label>
                        <Select
                          id="state"
                          className="text-lg"
                          {...form.register('address.state')}
                          disabled={!form.watch('address.country') || availableStates.length === 0}
                        >
                          {!form.watch('address.state') && <option value="">Select a state/province</option>}
                          {availableStates.map((state) => (
                            <option
                              key={state.code}
                              value={state.code}
                            >
                              {state.name}
                            </option>
                          ))}
                        </Select>
                      </Field>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <Field>
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          type="text"
                          {...form.register('address.city')}
                        />
                      </Field>

                      <Field>
                        <Label htmlFor="postcode">Postcode</Label>
                        <Input
                          id="postcode"
                          type="text"
                          {...form.register('address.postcode')}
                        />
                      </Field>
                    </div>
                  </div>
                </form>
              </DialogBodyAnimated>
            )}
          </div>
        )}
      </DialogBody>

      <DialogActions>
        <Button
          outline
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          className="transition-all duration-300 data-disabled:opacity-50 data-disabled:cursor-not-allowed"
          disabled={!form.formState.isValid || !selectedUser || isSubmitting}
          onClick={form.handleSubmit(onSubmit)}
          data-testid="add-cardholder-confirm"
        >
          {isSubmitting ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Adding...
            </div>
          ) : (
            'Add Cardholder'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddNewCardholderModal
